<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAccount } from 'dashboard/composables/useAccount';
import { LIA_UNAVAILABLE_REASONS } from 'dashboard/composables/captain/useLiaAvailability';
import AiIdentity from 'dashboard/components-next/ai/AiIdentity.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  reason: {
    type: String,
    default: LIA_UNAVAILABLE_REASONS.NOT_CONFIGURED,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const router = useRouter();
const { isAdmin } = useAdmin();
const { accountScopedRoute } = useAccount();

const isNoCredits = computed(
  () => props.reason === LIA_UNAVAILABLE_REASONS.NO_CREDITS
);
const title = computed(() =>
  isNoCredits.value
    ? t(
        'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.UNAVAILABLE.NO_CREDITS.TITLE'
      )
    : t(
        'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.UNAVAILABLE.NOT_CONFIGURED.TITLE'
      )
);
const description = computed(() => {
  if (isNoCredits.value) {
    return isAdmin.value
      ? t(
          'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.UNAVAILABLE.NO_CREDITS.ADMIN'
        )
      : t(
          'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.UNAVAILABLE.NO_CREDITS.AGENT'
        );
  }
  return isAdmin.value
    ? t(
        'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.UNAVAILABLE.NOT_CONFIGURED.ADMIN'
      )
    : t(
        'INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.UNAVAILABLE.NOT_CONFIGURED.AGENT'
      );
});

const goToOpenAIKey = () => {
  emit('close');
  router.push(
    accountScopedRoute('settings_applications_integration', {
      integration_id: 'openai',
    })
  );
};
</script>

<template>
  <div
    class="z-50 w-72 max-w-[calc(100vw-2rem)] rounded-xl border border-n-weak bg-n-solid-2 p-4 shadow-lg"
    role="dialog"
    :aria-label="title"
    @keydown.esc.stop="emit('close')"
  >
    <div class="flex items-start gap-3">
      <span
        class="grid size-10 shrink-0 place-content-center rounded-lg bg-n-alpha-2 text-xl text-n-slate-11"
      >
        <AiIdentity sleeping />
      </span>
      <div class="min-w-0">
        <p class="mb-1 text-sm font-semibold text-n-slate-12">
          {{ title }}
        </p>
        <p class="mb-0 text-xs leading-5 text-n-slate-11">
          {{ description }}
        </p>
      </div>
    </div>
    <Button
      v-if="isAdmin"
      class="mt-3 w-full"
      color="blue"
      size="sm"
      icon="i-hugeicons-key-01"
      :label="t('INTEGRATION_SETTINGS.OPEN_AI.REPLY_OPTIONS.UNAVAILABLE.CTA')"
      data-test="lia-configure-key"
      @click="goToOpenAIKey"
    />
  </div>
</template>
